import React, { useState } from 'react';
import { CompetitiveResult } from '../../types';
import { api } from '../../services/api';
import StoryCard from '../StoryCard';
import TabNavigation, { TabType } from '../TabNavigation';
import CompetitiveSimulation from '../simulations/CompetitiveSimulation';
import { modelStories } from '../../data/stories';
import { TrendingUp, TrendingDown } from 'lucide-react';

interface CompetitiveModuleProps {
  data: CompetitiveResult | null;
  onOptimizationComplete: (data: CompetitiveResult) => void;
  onLoadingStart: () => void;
}

const CompetitiveModule: React.FC<CompetitiveModuleProps> = ({
  data,
  onOptimizationComplete,
  onLoadingStart,
}) => {
  const [activeTab, setActiveTab] = useState<TabType>(data ? 'run' : 'case-study');

  const handleAnalyze = async () => {
    onLoadingStart();
    const result = await api.analyzeCompetition();
    onOptimizationComplete(result);
    setActiveTab('run');
  };

  return (
    <div className="space-y-6">
      <div className="section-card">
        <h2 className="text-3xl font-bold text-gray-900 mb-2">Competitive Intelligence</h2>
        <p className="text-gray-600 mb-4">
          Analyze market positioning, pricing strategy, and competitive dynamics
        </p>
        
        <TabNavigation activeTab={activeTab} onTabChange={setActiveTab} />
      </div>

      {/* Case Study Tab - Always show the story */}
      {activeTab === 'case-study' && (
        <div className="section-card">
          <StoryCard story={modelStories.competitive} />
        </div>
      )}

      {/* Run Tab */}
      {activeTab === 'run' && (
        <div className="space-y-6">
          <div className="section-card">
            <button type="button" onClick={handleAnalyze} className="btn-primary">Analyze Competition</button>
          </div>

      {data && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="stat-card">
              <div className="text-sm text-gray-600 mb-1">Market Share</div>
              <div className="text-2xl font-bold text-blue-600">{data.market_share.company}%</div>
            </div>
            <div className="stat-card">
              <div className="text-sm text-gray-600 mb-1">Growth Rate</div>
              <div className="text-2xl font-bold text-green-600 flex items-center">
                <TrendingUp className="w-5 h-5 mr-1" />
                {data.position.growth_rate}%
              </div>
            </div>
            <div className="stat-card">
              <div className="text-sm text-gray-600 mb-1">Recommended Price</div>
              <div className="text-2xl font-bold text-gray-800">${data.pricing.recommended}</div>
            </div>
            <div className="stat-card">
              <div className="text-sm text-gray-600 mb-1">Position</div>
              <div className="text-lg font-bold text-purple-600">{data.position.status}</div>
            </div>
          </div>

          {/* Market Share Comparison Table */}
          <div className="table-container">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">Market Share Analysis</h3>
            <table className="min-w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Company</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Market Share</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Change</th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Trend</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                <tr className="hover:bg-blue-50 bg-blue-50">
                  <td className="px-6 py-4 text-sm font-semibold text-gray-900">Your Company</td>
                  <td className="px-6 py-4 text-sm text-right font-bold text-blue-600">{data.market_share.company}%</td>
                  <td className="px-6 py-4 text-sm text-right text-green-600 font-semibold">+2.7%</td>
                  <td className="px-6 py-4 text-center">
                    <TrendingUp className="w-5 h-5 text-green-600 inline" />
                  </td>
                </tr>
                <tr className="hover:bg-gray-50">
                  <td className="px-6 py-4 text-sm text-gray-900">Competitor A</td>
                  <td className="px-6 py-4 text-sm text-right text-gray-900">{data.market_share.competitor_a}%</td>
                  <td className="px-6 py-4 text-sm text-right text-red-600">-1.2%</td>
                  <td className="px-6 py-4 text-center">
                    <TrendingDown className="w-5 h-5 text-red-600 inline" />
                  </td>
                </tr>
                <tr className="hover:bg-gray-50">
                  <td className="px-6 py-4 text-sm text-gray-900">Competitor B</td>
                  <td className="px-6 py-4 text-sm text-right text-gray-900">{data.market_share.competitor_b}%</td>
                  <td className="px-6 py-4 text-sm text-right text-green-600">+0.5%</td>
                  <td className="px-6 py-4 text-center">
                    <TrendingUp className="w-5 h-5 text-green-600 inline" />
                  </td>
                </tr>
                <tr className="hover:bg-gray-50">
                  <td className="px-6 py-4 text-sm text-gray-900">Others</td>
                  <td className="px-6 py-4 text-sm text-right text-gray-900">{data.market_share.others}%</td>
                  <td className="px-6 py-4 text-sm text-right text-red-600">-2.0%</td>
                  <td className="px-6 py-4 text-center">
                    <TrendingDown className="w-5 h-5 text-red-600 inline" />
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* Pricing Comparison Table */}
          <div className="table-container">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">Pricing Strategy</h3>
            <table className="min-w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Metric</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Current</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Recommended</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Impact</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                <tr className="hover:bg-gray-50">
                  <td className="px-6 py-4 text-sm font-medium text-gray-900">Base Price</td>
                  <td className="px-6 py-4 text-sm text-right text-gray-900">${data.pricing.current}</td>
                  <td className="px-6 py-4 text-sm text-right font-semibold text-blue-600">${data.pricing.recommended}</td>
                  <td className="px-6 py-4 text-sm text-right text-green-600">+3.3%</td>
                </tr>
                <tr className="hover:bg-gray-50">
                  <td className="px-6 py-4 text-sm font-medium text-gray-900">Market Average</td>
                  <td className="px-6 py-4 text-sm text-right text-gray-900">${data.pricing.competitor_avg}</td>
                  <td className="px-6 py-4 text-sm text-right text-gray-500">-</td>
                  <td className="px-6 py-4 text-sm text-right text-gray-500">Benchmark</td>
                </tr>
                <tr className="hover:bg-gray-50">
                  <td className="px-6 py-4 text-sm font-medium text-gray-900">Price Elasticity</td>
                  <td className="px-6 py-4 text-sm text-right text-gray-900">{data.pricing.elasticity}</td>
                  <td className="px-6 py-4 text-sm text-right text-gray-500">-</td>
                  <td className="px-6 py-4 text-sm text-right text-orange-600">Elastic</td>
                </tr>
              </tbody>
            </table>
          </div>

          {data.llm_explanation && (
            <div className="section-card bg-purple-50 border-l-4 border-purple-500">
              <h3 className="text-lg font-bold text-purple-900 mb-3">AI Analysis</h3>
              <p className="text-gray-700 leading-relaxed whitespace-pre-line">
                {data.llm_explanation}
              </p>
            </div>
          )}

          {data.recommendations && (
            <div className="success-section">
              <h3 className="text-lg font-bold text-green-900 mb-3">Strategic Recommendations</h3>
              <ol className="space-y-2">
                {data.recommendations.map((rec, idx) => (
                  <li key={idx} className="flex items-start">
                    <span className="font-bold text-green-700 mr-2">{idx + 1}.</span>
                    <span className="text-gray-700">{rec}</span>
                  </li>
                ))}
              </ol>
            </div>
          )}
        </>
      )}
        </div>
      )}

      {/* Simulation Tab */}
      {activeTab === 'simulation' && (
        <CompetitiveSimulation />
      )}
    </div>
  );
};

export default CompetitiveModule;
